import { BriefingImagePropertiesModel } from '@Common/PageBuilder/models/briefing-image-properties.model';
import { deliverySchedulingTypesEnum } from '../../../Common/Forms/enums/delivery-scheduling-types.enum';
import { MonitoringBackEndDataSourceModel } from '../../Shared/models/monitoring-back-end-data-source.model';
import { OnlineBriefingBannerStyleEnum } from '../enums/online-briefing-banner-style.enum';
import { onlineBriefingOptionDefault } from '../data/online-briefing-option-default';
import { OnlineBriefingOptionVersioner } from '../utils/online-briefing-option-versioner';
import { OnlineBriefingTemplateEnum } from '../enums/online-briefing-template.enum';
import { BrandIdentity } from '../../../Common/BrandIdentity/models/brand-identity';
import { BrandIdentityApi } from '@api/brandIdentity/models/brand-identity.api.model';
import { MonitoringKeywordGroupModel } from '../../Shared/models/monitoring-keyword-group.model';
import { MentionSearchCriteriaModel } from '../../Shared/models/mention-search-criteria.model';
import { MonitoringBrand } from '../../Shared/models/monitoring-brand.model';
import { QuarterlyDateRangeSettingsModel } from '../../Shared/models/quarterly-date-range-settings.model';
import { ImagePropertiesApiModel } from '@api/pagebuilder/models/image-properties-api.model';
import { MonthlyDateRangeSettingsModel } from '../../../Monitor/Shared/models/monthly-date-range-settings.model';

export type OnlineBriefingOptionVersion = '1.0';

export const currentOnlineBriefingOptionVersion: OnlineBriefingOptionVersion = '1.0';

export class OnlineBriefingTemplateConfigModel {
    version: OnlineBriefingOptionVersion = currentOnlineBriefingOptionVersion;
    template: OnlineBriefingTemplateEnum;
    deliverySchedulingType: deliverySchedulingTypesEnum;
    allowAi: boolean;
    isChanged: boolean = false;
    accentColor: string;
    bannerStyle: OnlineBriefingBannerStyleEnum;
    analysisFocus: string;
    dataSource: MonitoringBackEndDataSourceModel;
    competitors: MonitoringBrand[];
    name: string;
    logo: BriefingImagePropertiesModel[];
    brand: BrandIdentity;
    monthlySettings?: MonthlyDateRangeSettingsModel;
    quarterlySettings?: QuarterlyDateRangeSettingsModel;

    constructor(previewConfigJson?: string) {
        if (previewConfigJson) {
            const jsonObject = JSON.parse(previewConfigJson) as OnlineBriefingTemplateConfigModel;
            const migrated = new OnlineBriefingOptionVersioner().transformDataToNewestVersion(jsonObject);
            const updated = OnlineBriefingTemplateConfigModel.deepTransfer(migrated, OnlineBriefingTemplateConfigModel.deepClone(onlineBriefingOptionDefault));

            this.version = updated.version;
            this.template = updated.template;
            this.deliverySchedulingType = updated.deliverySchedulingType;
            this.allowAi = updated.allowAi;
            this.accentColor = updated.accentColor;
            this.bannerStyle = updated.bannerStyle;
            this.analysisFocus = updated.analysisFocus;
            this.dataSource = OnlineBriefingTemplateConfigModel.reviveDataSource(updated.dataSource);
            this.competitors = (updated.competitors ?? []).map(raw => OnlineBriefingTemplateConfigModel.reviveCompetitor(raw));
            this.name = updated.name;
            this.logo = OnlineBriefingTemplateConfigModel.reviveLogo(updated.logo);
            this.brand = OnlineBriefingTemplateConfigModel.reviveBrand(updated.brand);
            this.monthlySettings = OnlineBriefingTemplateConfigModel.reviveMonthlySettings(updated.monthlySettings);
            this.quarterlySettings = OnlineBriefingTemplateConfigModel.reviveQuarterlySettings(updated.quarterlySettings);
        } else {
            const defaultClone = OnlineBriefingTemplateConfigModel.deepClone(onlineBriefingOptionDefault);

            this.template = defaultClone.template;
            this.deliverySchedulingType = defaultClone.deliverySchedulingType;
            this.allowAi = defaultClone.allowAi;
            this.accentColor = defaultClone.accentColor;
            this.bannerStyle = defaultClone.bannerStyle;
            this.analysisFocus = defaultClone.analysisFocus;
            this.dataSource = defaultClone.dataSource;
            this.competitors = defaultClone.competitors;
            this.name = defaultClone.name;
            this.logo = defaultClone.logo;
            this.brand = defaultClone.brand;
            this.monthlySettings = defaultClone.monthlySettings;
            this.quarterlySettings = defaultClone.quarterlySettings;
        }
    }

    private static reviveMonthlySettings(obj: any): MonthlyDateRangeSettingsModel {
        const settings = new MonthlyDateRangeSettingsModel();
        if (obj) {
            settings.isCalendarMonths = obj.isCalendarMonths ?? true;
            settings.selectedMonth = obj.selectedMonth ?? null;
            settings.selectedYear = obj.selectedYear ?? null;
        }
        return settings;
    }

    private static reviveQuarterlySettings(obj: any): QuarterlyDateRangeSettingsModel {
        const settings = new QuarterlyDateRangeSettingsModel();
        if (obj) {
            settings.isCalendarQuarters = obj.isCalendarQuarters ?? true;
            settings.selectedQuarter = obj.selectedQuarter ?? null;
            settings.selectedYear = obj.selectedYear ?? null;
        }
        return settings;
    }

    private static deepTransfer<T>(jsonObject: T, defaultObject: T): T {
        if (Array.isArray(jsonObject)) {
            return jsonObject.map(item => this.deepClone(item)) as unknown as T;
        } else if (jsonObject === null || jsonObject === undefined) {
            return this.deepClone(defaultObject);
        } else if (defaultObject === null || defaultObject === undefined) {
            return this.deepClone(jsonObject);
        } else if (typeof jsonObject === 'object') {
            return Object.getOwnPropertyNames(defaultObject).reduce((o, prop) => {
                Object.defineProperty(
                    o,
                    prop,
                    Object.getOwnPropertyDescriptor(jsonObject, prop) ?? Object.getOwnPropertyDescriptor(defaultObject, prop)
                );
                o[prop] = this.deepTransfer(
                    (jsonObject as { [key: string]: any })[prop],
                    (defaultObject as { [key: string]: any })[prop]
                );
                return o;
            }, Object.create(Object.getPrototypeOf(defaultObject)));
        }
        return jsonObject as T;
    }

    private static deepClone<T>(source: T): T {
        if (Array.isArray(source)) {
            return source.map(item => this.deepClone(item)) as unknown as T;
        } else if (source && typeof source === 'object') {
            return Object.getOwnPropertyNames(source).reduce((o, prop) => {
                Object.defineProperty(o, prop, Object.getOwnPropertyDescriptor(source, prop));
                o[prop] = this.deepClone(source[prop]);
                return o;
            }, Object.create(Object.getPrototypeOf(source)));
        }
        return source as T;
    }

    private static reviveLogo(arr: any[]): BriefingImagePropertiesModel[] {
        return arr?.map(obj => {
            const logo = new BriefingImagePropertiesModel(obj as ImagePropertiesApiModel);
            if (obj.naturalWidth) {
                logo.naturalWidth = obj.naturalWidth;
            }
            if (obj.naturalHeight) {
                logo.naturalHeight = obj.naturalHeight;
            }
            return logo;
        }) ?? [];
    }

    private static reviveBrand(obj: any): BrandIdentity {
        if (!obj) {
            return null as unknown as BrandIdentity;
        }

        // Transfer the `BrandIdentity` JSON obj to `BrandIdentityApi`.
        const brandApi = {...obj, logo: obj.logo?.source} as BrandIdentityApi;
        const brand = new BrandIdentity(brandApi);
        return brand;
    }

    private static reviveDataSource(obj: any): MonitoringBackEndDataSourceModel {
        if (!obj) {
            return null as unknown as MonitoringBackEndDataSourceModel;
        }

        const instance = new MonitoringBackEndDataSourceModel(obj.type, obj.id);
        instance.dataSourceName = obj.dataSourceName;
        instance.referenceId = obj.referenceId;

        const filterCriteria = instance.filterSearchCriteria;
        if (filterCriteria && obj.filterSearchCriteria?.query) {
            Object.assign(filterCriteria.query, obj.filterSearchCriteria.query);
            // dateQuery dates survive JSON as ISO strings; revive them to Date objects so
            // downstream getApiEntity()/getTime() calls don't throw (e.g. quarterly absolute ranges)
            const dateQuery = filterCriteria.query.dateQuery;
            if (dateQuery) {
                if (dateQuery.absoluteDate) {
                    dateQuery.absoluteDate = new Date(dateQuery.absoluteDate);
                }
                if (dateQuery.absoluteRange?.start) {
                    dateQuery.absoluteRange.start = new Date(dateQuery.absoluteRange.start);
                }
                if (dateQuery.absoluteRange?.end) {
                    dateQuery.absoluteRange.end = new Date(dateQuery.absoluteRange.end);
                }
            }
            // keywordGroups from JSON are plain objects; revive as class instances so isEmpty() works
            filterCriteria.query.keywordGroups = filterCriteria.query.keywordGroups?.length
                ? filterCriteria.query.keywordGroups.map(g => Object.assign(new MonitoringKeywordGroupModel(), g))
                : [new MonitoringKeywordGroupModel()];
            // sanitize engagementFilter — partial values (e.g. min without max) cause API 400 errors
            const ef = filterCriteria.query.engagementFilter;
            if (ef) {
                for (const key of Object.keys(ef) as (keyof typeof ef)[]) {
                    if (ef[key]?.min == null || ef[key]?.max == null) {
                        ef[key] = {};
                    }
                }
            }
            if (obj.filterSearchCriteria.showSimilar != null) {
                filterCriteria.showSimilar = obj.filterSearchCriteria.showSimilar;
            }
            if (obj.filterSearchCriteria.savedFilterIds?.length) {
                filterCriteria.savedFilterIds = obj.filterSearchCriteria.savedFilterIds;
            }
        }

        return instance;
    }

    private static reviveCompetitor(raw: any): MonitoringBrand {
        const competitor = new MonitoringBrand();
        competitor.brand = OnlineBriefingTemplateConfigModel.reviveBrand(raw.brand);
        competitor.name = raw.name;
        competitor.color = raw.color;
        competitor.logo = OnlineBriefingTemplateConfigModel.reviveLogo(raw.logo);
        competitor.dataSource = OnlineBriefingTemplateConfigModel.reviveDataSource(raw.dataSource);
        return competitor;
    }

    public equals(other: OnlineBriefingTemplateConfigModel | null): boolean {
        if (!other) {
            return false;
        }
        if (this.template !== other.template) {
            return false;
        }
        if (this.deliverySchedulingType !== other.deliverySchedulingType) {
            return false;
        }
        if (this.allowAi !== other.allowAi) {
            return false;
        }
        if (this.accentColor !== other.accentColor) {
            return false;
        }
        if (this.bannerStyle !== other.bannerStyle) {
            return false;
        }
        if (this.analysisFocus !== other.analysisFocus) {
            return false;
        }
        if (this.name !== other.name) {
            return false;
        }
        if (this.logo?.length !== other.logo?.length) {
            return false;
        }
        if (this.logo?.some((logo, i) => !logo.equals(other.logo[i]))) {
            return false;
        }
        if (this.brand?.id !== other.brand?.id) {
            return false;
        }
        if (this.dataSource?.type !== other.dataSource?.type) {
            return false;
        }
        if (this.dataSource?.id !== other.dataSource?.id) {
            return false;
        }
        const thisFSC = this.dataSource?.filterSearchCriteria;
        const otherFSC = other.dataSource?.filterSearchCriteria;
        // For the quarterly template the filter date range is fully derived from quarterlySettings
        // (compared below) — there is no free-form date picker. The derived absoluteRange is
        // materialized through several code paths that can disagree on timezone basis (UTC preference
        // vs browser-local), so comparing it directly produces a spurious "pending changes" banner.
        // Ignore the date here for quarterly only; other templates still compare their real date input.
        const ignoreDerivedDate = this.template === OnlineBriefingTemplateEnum.ONLINE_BRIEFING_TEMPLATE_QUARTERLY;
        if (!!thisFSC !== !!otherFSC || (thisFSC && !thisFSC.equals(otherFSC as MentionSearchCriteriaModel, ignoreDerivedDate))) {
            return false;
        }
        const thisMonthly = this.monthlySettings;
        const otherMonthly = other.monthlySettings;
        if ((thisMonthly?.isCalendarMonths ?? true) !== (otherMonthly?.isCalendarMonths ?? true)
            || (thisMonthly?.selectedMonth ?? null) !== (otherMonthly?.selectedMonth ?? null)
            || (thisMonthly?.selectedYear ?? null) !== (otherMonthly?.selectedYear ?? null)
        ) {
            return false;
        }
        const thisQuarterly = this.quarterlySettings;
        const otherQuarterly = other.quarterlySettings;
        if ((thisQuarterly?.isCalendarQuarters ?? true) !== (otherQuarterly?.isCalendarQuarters ?? true)
            || (thisQuarterly?.selectedQuarter ?? null) !== (otherQuarterly?.selectedQuarter ?? null)
            || (thisQuarterly?.selectedYear ?? null) !== (otherQuarterly?.selectedYear ?? null)
        ) {
            return false;
        }
        const thisCount = this.competitors?.length ?? 0;
        const otherCount = other.competitors?.length ?? 0;
        if (thisCount !== otherCount) {
            return false;
        }
        for (let i = 0; i < thisCount; i++) {
            const a = this.competitors[i];
            const b = other.competitors[i];
            if (a.name !== b.name
                || a.color !== b.color
                || a.brand?.id !== b.brand?.id
                || a.dataSource?.type !== b.dataSource?.type
                || a.dataSource?.id !== b.dataSource?.id
            ) {
                return false;
            }
        }
        return true;
    }

    public toApiString(): string {
        return JSON.stringify({
            version: this.version,
            template: this.template,
            deliverySchedulingType: this.deliverySchedulingType,
            allowAi: this.allowAi,
            accentColor: this.accentColor,
            bannerStyle: this.bannerStyle,
            analysisFocus: this.analysisFocus,
            dataSource: this.dataSource ? {
                type: this.dataSource.type,
                id: this.dataSource.id,
                dataSourceName: this.dataSource.dataSourceName,
                referenceId: this.dataSource.referenceId,
                filterSearchCriteria: this.dataSource.filterSearchCriteria ? {
                    query: this.dataSource.filterSearchCriteria.query,
                    showSimilar: this.dataSource.filterSearchCriteria.showSimilar,
                    savedFilterIds: this.dataSource.filterSearchCriteria.savedFilterIds,
                } : null,
            } : null,
            competitors: this.competitors,
            name: this.name,
            logo: this.logo,
            brand: this.brand,
            monthlySettings: this.monthlySettings,
            quarterlySettings: this.quarterlySettings
        });
    }
}
